import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import AssetsSchemaIcon from '@atlaskit/icon-lab/core/assets-schema';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientSearchResultType } from '@atlassian/search-client/common/types';
import { getGenericUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getAssetsEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchAssetsObject_AtlassianSearchPage$key } from './__generated__/searchAssetsObject_AtlassianSearchPage.graphql';

const SchemaMetadata = ({ schemaName }: { schemaName: string }) => {
	return (
		<Inline space="space.050" alignBlock="center">
			<AssetsSchemaIcon size="small" label="" />
			<Text color="inherit">{schemaName}</Text>
		</Inline>
	);
};

export const SearchAssetsObject = ({
	node,
	position,
	searchSessionId,
	primaryProduct,
	selectedProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchAssetsObject_AtlassianSearchPage$key>): React.JSX.Element | null => {
	const data = useFragment(
		graphql`
			fragment searchAssetsObject_AtlassianSearchPage on SearchResultAssetsObject {
				__typename
				id
				title
				description
				type
				lastModifiedDate
				objectSchemaEntity {
					name
				}
				objectEntity {
					avatar {
						url48
					}
					links {
						web
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const url = useMemo(() => {
		const webUrl = data.objectEntity?.links?.web;
		if (!webUrl) {
			return null;
		}
		return getGenericUrlWithAnalyticsQueryParams({
			url: webUrl,
			searchSessionId,
		});
	}, [data.objectEntity?.links?.web, searchSessionId]);

	const getLastModified = useMemo(() => {
		if (!data.lastModifiedDate) {
			return null;
		}
		return <LastModified lastModified={data.lastModifiedDate} />;
	}, [data.lastModifiedDate]);

	const headerMetadataParts = [
		['type', <SearchResultType type={data.type as SearchClientSearchResultType} />],
		...(data.objectSchemaEntity?.name
			? [['schema', <SchemaMetadata schemaName={data.objectSchemaEntity.name} />]]
			: []),
		['lastModified', getLastModified],
	];

	const enrichedTitle = useMemo(() => {
		return enrichExcerpt(data.title);
	}, [data.title]);

	const enrichedDescription = useMemo(() => {
		return enrichExcerpt(data.description);
	}, [data.description]);

	const icon = useMemo(() => {
		return getAssetsEntityIcon({
			iconUrl: data.objectEntity?.avatar?.url48,
			type: data.type,
			size: 'medium',
		});
	}, [data.objectEntity?.avatar?.url48, data.type]);

	if (!url) {
		return null;
	}

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Assets}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			title={enrichedTitle}
			description={<DescriptionBody description={enrichedDescription} />}
			position={position}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchAssetsObject = (
	node?: any,
): node is searchAssetsObject_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultAssetsObject';
};
